import React, { useState } from 'react';
import { UserProfile, signInWithGoogle, logoutUser } from '../lib/firebase';
import { Language } from '../types/kundali';
import { sanitizeSessionUser } from '../utils/rbac';
import {
  ShieldCheck,
  UserCheck,
  Sparkles,
  Mail,
  Lock,
  KeyRound,
  ArrowRight,
  LogOut,
  X,
  CheckCircle2,
  Copy,
  Info,
  Shield,
  User,
  RefreshCw,
  Phone,
  Eye,
  EyeOff,
  UserPlus,
  LogIn,
  Send
} from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile | null;
  onLoginSuccess: (user: UserProfile) => void;
  lang?: Language;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onLoginSuccess,
  lang = 'en'
}) => {
  // Active Tab: 'login' | 'register'
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  // Login Options
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp' | 'google'>('password');
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // OTP Login Sub-state
  const [otpTargetChannel, setOtpTargetChannel] = useState<'mobile' | 'email'>('mobile');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [loginOtpStep, setLoginOtpStep] = useState<'input' | 'verify'>('input');
  const [loginOtpCode, setLoginOtpCode] = useState('');
  const [generatedLoginOtp, setGeneratedLoginOtp] = useState<string | null>(null);

  // Register Fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regMobile, setRegMobile] = useState('');
  const [regRole, setRegRole] = useState<'user' | 'super_user' | 'admin'>('user');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regOtpStep, setRegOtpStep] = useState<'send' | 'verify'>('send');
  const [regMobileOtp, setRegMobileOtp] = useState('');
  const [generatedRegOtp, setGeneratedRegOtp] = useState<string | null>(null);
  const [mobileVerified, setMobileVerified] = useState(false);

  // Status & Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  // --- HANDLERS FOR REGISTER ---
  const handleSendRegMobileOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!regMobile || regMobile.replace(/\D/g, '').length < 7) {
      setError(lang === 'hi' ? 'कृपया एक वैध 10-अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: regMobile.trim(),
          channel: 'mobile'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send Mobile OTP');

      setGeneratedRegOtp(data.otpCode);
      setRegOtpStep('verify');
      setSuccessMsg(lang === 'hi' ? 'मोबाइल सत्यापन OTP कोड भेजा गया है' : 'Mobile verification code sent!');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code to mobile number.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegMobileOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regMobileOtp || regMobileOtp.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter the 6-digit OTP code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: regMobile.trim(),
          code: regMobileOtp.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid OTP code');

      setMobileVerified(true);
      setSuccessMsg(lang === 'hi' ? 'मोबाइल नंबर सफलतापूर्वक सत्यापित हो गया!' : 'Mobile number verified successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to verify mobile OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!regName.trim()) {
      setError(lang === 'hi' ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your full name');
      return;
    }
    if (!regEmail || !regEmail.includes('@')) {
      setError(lang === 'hi' ? 'कृपया एक वैध ईमेल पता दर्ज करें' : 'Please enter a valid email address');
      return;
    }
    if (!regMobile || regMobile.replace(/\D/g, '').length < 7) {
      setError(lang === 'hi' ? 'कृपया अपना मोबाइल नंबर दर्ज करें' : 'Please enter your mobile number');
      return;
    }
    if (!regPassword || regPassword.length < 6) {
      setError(lang === 'hi' ? 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए' : 'Password must be at least 6 characters');
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError(lang === 'hi' ? 'पासवर्ड मेल नहीं खा रहे हैं' : 'Passwords do not match');
      return;
    }
    if (!regMobileOtp || regMobileOtp.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया पहले मोबाइल नंबर पर प्राप्त 6-अंकों का OTP कोड भरें' : 'Please verify mobile number with 6-digit OTP code first');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: regName.trim(),
          email: regEmail.trim(),
          mobile: regMobile.trim(),
          password: regPassword,
          mobileOtp: regMobileOtp.trim(),
          preferredRole: regRole
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      localStorage.setItem('astro_custom_session', JSON.stringify(userProfile));
      onLoginSuccess(userProfile);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS FOR LOGIN ---
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginIdentifier.trim()) {
      setError(lang === 'hi' ? 'कृपया ईमेल अथवा मोबाइल नंबर दर्ज करें' : 'Please enter your User ID, Email or Mobile number');
      return;
    }
    if (!loginPassword) {
      setError(lang === 'hi' ? 'कृपया अपना पासवर्ड दर्ज करें' : 'Please enter your password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: loginIdentifier.trim(),
          password: loginPassword
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      localStorage.setItem('astro_custom_session', JSON.stringify(userProfile));
      onLoginSuccess(userProfile);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to log in with password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLoginOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpIdentifier.trim()) {
      setError(
        otpTargetChannel === 'mobile'
          ? (lang === 'hi' ? 'कृपया मोबाइल नंबर दर्ज करें' : 'Please enter your mobile number')
          : (lang === 'hi' ? 'कृपया ईमेल पता दर्ज करें' : 'Please enter your email address')
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: otpTargetChannel === 'email' ? otpIdentifier.trim() : undefined,
          mobile: otpTargetChannel === 'mobile' ? otpIdentifier.trim() : undefined,
          channel: otpTargetChannel
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');

      setGeneratedLoginOtp(data.otpCode);
      setLoginOtpStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginOtpCode || loginOtpCode.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter the 6-digit OTP code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: otpIdentifier.trim(),
          code: loginOtpCode.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid OTP code');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      localStorage.setItem('astro_custom_session', JSON.stringify(userProfile));
      onLoginSuccess(userProfile);
      onClose();
    } catch (err: any) {
      setError(err.message || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      const userProfile = sanitizeSessionUser(user) || user;
      localStorage.setItem('astro_custom_session', JSON.stringify(userProfile));
      onLoginSuccess(userProfile);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-astro-primary/20 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-[#fdfaf6] border border-astro-border w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-xl relative my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-astro-secondary-text hover:text-astro-text p-2 rounded-xl bg-astro-secondary-surface border border-astro-border transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Current User Logged In Card */}
        {currentUser ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-amber-800/20 border-2 border-amber-800 mx-auto flex items-center justify-center text-astro-secondary text-2xl font-bold">
              {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : 'U'}
            </div>

            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-800/10 border border-amber-800/30 text-astro-secondary text-xs font-semibold mb-2">
                {currentUser.role === 'admin' ? (
                  <>
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {lang === 'hi' ? 'एडमिन प्रोफाइल' : 'Admin Profile'}
                  </>
                ) : (
                  <>
                    <UserCheck className="w-3.5 h-3.5" />
                    {lang === 'hi' ? 'सामान्य यूजर प्रोफाइल' : 'General Profile'}
                  </>
                )}
              </div>
              <h3 className="text-xl font-bold text-astro-text">{currentUser.displayName}</h3>
              <p className="text-sm text-astro-secondary-text font-mono mt-0.5">{currentUser.email}</p>
            </div>

            <div className="p-4 bg-astro-secondary-surface rounded-2xl border border-astro-border text-left text-xs text-astro-secondary-text space-y-2">
              <div className="flex justify-between">
                <span>Account Provider:</span>
                <span className="font-semibold text-astro-text uppercase">{currentUser.authProvider}</span>
              </div>
              <div className="flex justify-between">
                <span>Access Scope:</span>
                <span className="font-semibold text-astro-secondary">
                  {currentUser.role === 'admin'
                    ? 'Full Vault Rights (All Kundalis Access)'
                    : 'Personal Saved Kundalis Only'}
                </span>
              </div>
            </div>

            <button
              onClick={async () => {
                await logoutUser();
                onLoginSuccess(null as any);
              }}
              className="w-full py-3 bg-red-500/10 border border-red-500/30 text-red-400 font-semibold rounded-xl hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {lang === 'hi' ? 'लॉगआउट करें' : 'Sign Out Account'}
            </button>
          </div>
        ) : (
          <div>
            {/* Header Title */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl bg-amber-800/10 border border-amber-800/30 flex items-center justify-center text-astro-secondary mx-auto mb-3">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-astro-text font-serif">
                {lang === 'hi' ? 'वेदांग ज्योतिष अकाउंट' : 'Vedanga Jyotisha Account'}
              </h3>
              <p className="text-xs text-astro-secondary-text mt-1">
                {lang === 'hi'
                  ? 'अकाउंट बनाएं या पासवर्ड/OTP द्वारा लॉग-इन करें'
                  : 'Register a new profile or sign in via Password/OTP'}
              </p>
            </div>

            {/* Mode Switcher: Login vs Register */}
            <div className="flex bg-astro-secondary-surface p-1.5 rounded-2xl border border-astro-border mb-5">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('login');
                  setError(null);
                  setSuccessMsg(null);
                }}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === 'login'
                    ? 'bg-amber-800 text-astro-text shadow-md'
                    : 'text-astro-muted hover:text-astro-text'
                }`}
              >
                <LogIn className="w-3.5 h-3.5" />
                <span>{lang === 'hi' ? 'साइन-इन' : 'Sign In'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveTab('register');
                  setError(null);
                  setSuccessMsg(null);
                }}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === 'register'
                    ? 'bg-amber-800 text-astro-text shadow-md'
                    : 'text-astro-muted hover:text-astro-text'
                }`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>{lang === 'hi' ? 'रजिस्ट्रेशन' : 'Register'}</span>
              </button>
            </div>

            {/* Error & Success Alerts */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs flex items-center gap-2">
                <Info className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {successMsg && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* TAB 1: REGISTRATION */}
            {activeTab === 'register' && (
              <form onSubmit={handleRegisterUser} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                    {lang === 'hi' ? 'पूरा नाम *' : 'Full Name *'}
                  </label>
                  <div className="relative">
                    <User className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                    <input
                      type="text"
                      required
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder="e.g. Rahul Sharma"
                      className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                    {lang === 'hi' ? 'ईमेल पता *' : 'Email Address *'}
                  </label>
                  <div className="relative">
                    <Mail className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                    <input
                      type="email"
                      required
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="rahul.sharma@example.com"
                      className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none font-mono"
                    />
                  </div>
                </div>

                {/* Mobile Number & Send OTP */}
                <div>
                  <label className="block text-xs font-semibold text-astro-muted mb-1">
                    {lang === 'hi' ? 'मोबाइल नंबर (OTP सत्यापन के साथ) *' : 'Mobile Number (with OTP verification) *'}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Phone className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                      <input
                        type="tel"
                        required
                        value={regMobile}
                        onChange={(e) => {
                          setRegMobile(e.target.value);
                          setMobileVerified(false);
                        }}
                        placeholder="9876543210"
                        className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSendRegMobileOtp}
                      disabled={loading || !regMobile}
                      className="px-3 py-2.5 bg-amber-800/10 hover:bg-amber-800/20 border border-amber-800/40 text-astro-secondary font-semibold rounded-xl text-xs transition-all shrink-0 flex items-center gap-1"
                    >
                      <Send className="w-3 h-3" />
                      <span>{regOtpStep === 'verify' ? 'Resend OTP' : 'Send OTP'}</span>
                    </button>
                  </div>

                  {generatedRegOtp && (
                    <div className="mt-2 p-2 bg-amber-800/10 border border-amber-800/30 rounded-xl flex items-center justify-between text-xs text-astro-secondary">
                      <span>
                        OTP Code: <strong className="font-mono text-astro-text">{generatedRegOtp}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => setRegMobileOtp(generatedRegOtp)}
                        className="text-[10px] bg-amber-800/20 px-2 py-0.5 rounded text-astro-secondary hover:bg-amber-800 hover:text-astro-text font-semibold transition-all"
                      >
                        Auto-fill
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile OTP Input */}
                <div>
                  <label className="block text-xs font-semibold text-astro-muted mb-1">
                    {lang === 'hi' ? 'मोबाइल 6-अंकों का OTP कोड *' : '6-Digit Mobile Verification OTP *'}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={regMobileOtp}
                      onChange={(e) => setRegMobileOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 123456"
                      className="flex-1 px-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-secondary font-mono font-bold tracking-widest focus:border-amber-800 focus:outline-none"
                    />
                    {regOtpStep === 'verify' && !mobileVerified && (
                      <button
                        type="button"
                        onClick={handleVerifyRegMobileOtp}
                        disabled={loading || regMobileOtp.length !== 6}
                        className="px-3 py-2.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-semibold rounded-xl text-xs transition-all shrink-0"
                      >
                        Verify
                      </button>
                    )}
                  </div>
                </div>

                {/* Profile Tier Selector */}
                <div>
                  <label className="block text-xs font-semibold text-astro-muted mb-1">
                    {lang === 'hi' ? 'प्रोफ़ाइल का प्रकार (Profile Tier)' : 'Select Profile Type'}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setRegRole('user')}
                      className={`p-2 rounded-xl border text-xs font-bold transition-all text-center ${
                        regRole === 'user'
                          ? 'bg-amber-800/20 border-amber-800 text-astro-secondary'
                          : 'bg-astro-secondary-surface border-astro-border text-astro-muted'
                      }`}
                    >
                      Normal User
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegRole('super_user')}
                      className={`p-2 rounded-xl border text-xs font-bold transition-all text-center ${
                        regRole === 'super_user'
                          ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                          : 'bg-astro-secondary-surface border-astro-border text-astro-muted'
                      }`}
                    >
                      Super User
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegRole('admin')}
                      className={`p-2 rounded-xl border text-xs font-bold transition-all text-center ${
                        regRole === 'admin'
                          ? 'bg-astro-secondary-surface0/20 border-astro-accent text-amber-400'
                          : 'bg-astro-secondary-surface border-astro-border text-astro-muted'
                      }`}
                    >
                      Admin Profile
                    </button>
                  </div>
                </div>

                {/* Password Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="block text-xs font-semibold text-astro-muted mb-1">
                      {lang === 'hi' ? 'पासवर्ड बनाएं *' : 'Create Password *'}
                    </label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                      <input
                        type={showRegPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-8 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        className="absolute right-2.5 top-2.5 text-astro-muted hover:text-astro-text"
                      >
                        {showRegPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-astro-muted mb-1">
                      {lang === 'hi' ? 'पासवर्ड की पुष्टि करें *' : 'Confirm Password *'}
                    </label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                      <input
                        type={showRegPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-amber-800 text-astro-text font-bold rounded-xl text-xs hover:bg-[#d4b46a] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg mt-2"
                >
                  {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  <span>{loading ? 'Creating Account...' : 'Complete Registration'}</span>
                </button>
              </form>
            )}

            {/* TAB 2: SIGN IN */}
            {activeTab === 'login' && (
              <div>
                <div className="flex bg-astro-secondary-surface p-1 rounded-xl border border-astro-border mb-4">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('password')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1 ${
                      loginMethod === 'password'
                        ? 'bg-amber-800 text-astro-text shadow'
                        : 'text-astro-muted hover:text-astro-text'
                    }`}
                  >
                    <Lock className="w-3 h-3" />
                    <span>Password</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setLoginMethod('otp')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1 ${
                      loginMethod === 'otp'
                        ? 'bg-amber-800 text-astro-text shadow'
                        : 'text-astro-muted hover:text-astro-text'
                    }`}
                  >
                    <KeyRound className="w-3 h-3" />
                    <span>OTP</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      handleGoogleSignIn();
                    }}
                    disabled={loading}
                    className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1 text-astro-muted hover:text-astro-text hover:bg-astro-card/60"
                    title="Sign In directly with Google"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.96H1.29v3.15C3.26 21.3 7.31 24 12 24z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.28 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.39l3.99-3.15z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.15c.95-2.85 3.6-4.96 6.72-4.96z"
                      />
                    </svg>
                    <span>{loading ? 'Connecting...' : 'Google'}</span>
                  </button>
                </div>

                {loginMethod === 'password' && (
                  <form onSubmit={handlePasswordLogin} className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-astro-muted mb-1">
                        {lang === 'hi' ? 'ईमेल अथवा मोबाइल नंबर *' : 'Email or Mobile Number *'}
                      </label>
                      <div className="relative">
                        <User className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                        <input
                          type="text"
                          required
                          value={loginIdentifier}
                          onChange={(e) => setLoginIdentifier(e.target.value)}
                          placeholder="email@example.com or 9876543210"
                          className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-astro-muted mb-1">
                        {lang === 'hi' ? 'पासवर्ड *' : 'Password *'}
                      </label>
                      <div className="relative">
                        <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                        <input
                          type={showLoginPassword ? 'text' : 'password'}
                          required
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full pl-9 pr-8 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-2.5 top-2.5 text-astro-muted hover:text-astro-text"
                        >
                          {showLoginPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-amber-800 text-astro-text font-bold rounded-xl text-xs hover:bg-[#d4b46a] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg mt-2"
                    >
                      {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                      <span>{loading ? 'Logging in...' : 'Sign In with Password'}</span>
                    </button>
                  </form>
                )}

                {loginMethod === 'otp' && (
                  <div>
                    <div className="flex justify-center gap-4 mb-3 text-xs">
                      <label className="flex items-center gap-1.5 cursor-pointer text-astro-muted">
                        <input
                          type="radio"
                          name="modalOtpChannel"
                          checked={otpTargetChannel === 'mobile'}
                          onChange={() => {
                            setOtpTargetChannel('mobile');
                            setLoginOtpStep('input');
                          }}
                          className="accent-[#c4a45a]"
                        />
                        <span>OTP on Mobile</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer text-astro-muted">
                        <input
                          type="radio"
                          name="modalOtpChannel"
                          checked={otpTargetChannel === 'email'}
                          onChange={() => {
                            setOtpTargetChannel('email');
                            setLoginOtpStep('input');
                          }}
                          className="accent-[#c4a45a]"
                        />
                        <span>OTP on Email</span>
                      </label>
                    </div>

                    {loginOtpStep === 'input' ? (
                      <form onSubmit={handleSendLoginOtp} className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-astro-muted mb-1">
                            {otpTargetChannel === 'mobile' ? 'Mobile Number *' : 'Email Address *'}
                          </label>
                          <div className="relative">
                            {otpTargetChannel === 'mobile' ? (
                              <Phone className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                            ) : (
                              <Mail className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-muted" />
                            )}
                            <input
                              type={otpTargetChannel === 'email' ? 'email' : 'tel'}
                              required
                              value={otpIdentifier}
                              onChange={(e) => setOtpIdentifier(e.target.value)}
                              placeholder={otpTargetChannel === 'mobile' ? '9876543210' : 'user@example.com'}
                              className="w-full pl-9 pr-3.5 py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-xs text-astro-text focus:border-amber-800 focus:outline-none font-mono"
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full py-3 bg-amber-800 text-astro-text font-bold rounded-xl text-xs hover:bg-[#d4b46a] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg"
                        >
                          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                          <span>{loading ? 'Generating OTP...' : `Send OTP to ${otpTargetChannel === 'mobile' ? 'Mobile' : 'Email'}`}</span>
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyLoginOtp} className="space-y-3">
                        {generatedLoginOtp && (
                          <div className="p-3 bg-amber-800/10 border border-amber-800/30 rounded-xl flex items-center justify-between text-xs text-astro-secondary">
                            <div>
                              <span className="font-semibold block text-[10px]">OTP Code:</span>
                              <span className="font-mono text-sm font-bold tracking-widest text-astro-text">{generatedLoginOtp}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setLoginOtpCode(generatedLoginOtp)}
                              className="px-2.5 py-1 bg-amber-800/20 border border-amber-800/40 text-astro-secondary hover:bg-amber-800 hover:text-astro-text font-semibold rounded-lg transition-all text-[10px]"
                            >
                              Auto-Fill
                            </button>
                          </div>
                        )}

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-semibold text-astro-muted">Enter 6-Digit OTP</label>
                            <button
                              type="button"
                              onClick={() => setLoginOtpStep('input')}
                              className="text-[10px] text-astro-secondary hover:underline"
                            >
                              Change Target
                            </button>
                          </div>
                          <input
                            type="text"
                            required
                            maxLength={6}
                            value={loginOtpCode}
                            onChange={(e) => setLoginOtpCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="123456"
                            className="w-full text-center tracking-[0.5em] text-lg font-mono py-2.5 bg-astro-secondary-surface border border-astro-border rounded-xl text-astro-secondary font-bold focus:border-amber-800 focus:outline-none"
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full py-3 bg-amber-800 text-astro-text font-bold rounded-xl text-xs hover:bg-[#d4b46a] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg"
                        >
                          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                          <span>{loading ? 'Verifying...' : 'Verify OTP & Log In'}</span>
                        </button>
                      </form>
                    )}
                  </div>
                )}

                {loginMethod === 'google' && (
                  <div className="p-4 bg-astro-secondary-surface rounded-2xl border border-astro-border text-center">
                    <button
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      className="w-full py-3 px-4 bg-[#e0e0e0] text-astro-text font-bold rounded-xl hover:bg-white transition-all flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 text-xs"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.96H1.29v3.15C3.26 21.3 7.31 24 12 24z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.28 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.39l3.99-3.15z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.15c.95-2.85 3.6-4.96 6.72-4.96z"
                        />
                      </svg>
                      {loading ? 'Authenticating...' : 'Sign In with Google'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

